import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import MediaCard from "@/components/MediaCard";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, Bell, Loader2, Share2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Favorites() {
  const { user, ready } = useAuth();
  const [favorites, setFavorites] = useState(null);
  const [follows, setFollows] = useState([]);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/me/favorites").then((r) => setFavorites(r.data));
    api.get("/me/follows").then((r) => setFollows(r.data));
  }, [user]);

  const shareList = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/me/favorites/share");
      const url = `${window.location.origin}/f/${data.token}`;
      if (navigator.share) {
        try { await navigator.share({ title: "Mes chaînes favorites — RDC", text: "Découvrez mes chaînes TV & radio favorites", url }); }
        catch { /* cancelled */ }
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien de partage copié !");
      }
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSharing(false); }
  };

  if (ready && !user) return (
    <div className="max-w-lg mx-auto px-4 text-center py-24">
      <Heart className="mx-auto text-rdcred mb-3" size={40} />
      <h1 className="font-heading font-bold text-2xl">Vos favoris</h1>
      <p className="text-muted-foreground mt-2">Connectez-vous pour retrouver vos médias préférés.</p>
      <Link to="/login" className="inline-flex mt-5 rounded-full bg-primary text-white px-6 py-2.5 font-semibold">Connexion</Link>
    </div>
  );

  if (favorites === null) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-primary" size={32} /></div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
      <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
        <h1 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">Ma bibliothèque</h1>
        {favorites.length > 0 && (
          <button onClick={shareList} disabled={sharing} data-testid="share-favorites-btn"
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-semibold hover:border-primary/50 transition-colors disabled:opacity-60">
            {sharing ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />} Partager ma liste
          </button>
        )}
      </div>
      <Tabs defaultValue="favorites">
        <TabsList className="mb-5">
          <TabsTrigger value="favorites" data-testid="tab-favorites"><Heart size={15} className="mr-1.5" /> Favoris ({favorites.length})</TabsTrigger>
          <TabsTrigger value="follows" data-testid="tab-follows"><Bell size={15} className="mr-1.5" /> Suivis ({follows.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="favorites">
          {favorites.length === 0 ? <p className="text-muted-foreground text-center py-16">Aucun favori pour l'instant. Ajoutez vos médias préférés ❤️</p> :
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">{favorites.map((m, i) => <MediaCard key={m.id} media={m} index={i} />)}</div>}
        </TabsContent>
        <TabsContent value="follows">
          {follows.length === 0 ? <p className="text-muted-foreground text-center py-16">Vous ne suivez aucun média pour l'instant.</p> :
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">{follows.map((m, i) => <MediaCard key={m.id} media={m} index={i} />)}</div>}
        </TabsContent>
      </Tabs>
    </div>
  );
}
